import { supabase } from '@/lib/supabase';

export interface AccountOperation {
  accountId: string;
  action: 'enable' | 'disable' | 'disconnect';
}

export interface BulkOperationResult {
  success: number;
  failed: number;
  errors: string[];
}

/**
 * Enable or disable a single bot account
 */
export const updateAccountStatus = async (
  accountId: string, 
  status: 'active' | 'inactive'
): Promise<void> => {
  const { error } = await supabase
    .from('bot_accounts')
    .update({ status })
    .eq('id', accountId);

  if (error) {
    throw new Error(`Failed to update account status: ${error.message}`);
  }
};

/**
 * Disconnect a single bot account
 */
export const disconnectAccount = async (accountId: string): Promise<void> => {
  const { error } = await supabase
    .from('bot_accounts')
    .delete()
    .eq('id', accountId);

  if (error) {
    throw new Error(`Failed to disconnect account: ${error.message}`);
  }
};

/**
 * Perform bulk operations on multiple accounts
 */
export const performBulkAccountOperations = async (
  operations: AccountOperation[]
): Promise<BulkOperationResult> => {
  const result: BulkOperationResult = {
    success: 0,
    failed: 0,
    errors: []
  };

  // Group operations by type for batch processing
  const enableOperations = operations.filter(op => op.action === 'enable');
  const disableOperations = operations.filter(op => op.action === 'disable');
  const disconnectOperations = operations.filter(op => op.action === 'disconnect');

  // Process enable operations
  if (enableOperations.length > 0) {
    try {
      const { error } = await supabase
        .from('bot_accounts')
        .update({ status: 'active' })
        .in('id', enableOperations.map(op => op.accountId));

      if (error) {
        result.failed += enableOperations.length;
        result.errors.push(`Enable operations failed: ${error.message}`);
      } else {
        result.success += enableOperations.length;
      }
    } catch (error) {
      result.failed += enableOperations.length;
      result.errors.push(`Enable operations error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Process disable operations
  if (disableOperations.length > 0) {
    try {
      const { error } = await supabase
        .from('bot_accounts')
        .update({ status: 'inactive' })
        .in('id', disableOperations.map(op => op.accountId));

      if (error) {
        result.failed += disableOperations.length;
        result.errors.push(`Disable operations failed: ${error.message}`);
      } else {
        result.success += disableOperations.length;
      }
    } catch (error) {
      result.failed += disableOperations.length;
      result.errors.push(`Disable operations error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Process disconnect operations
  if (disconnectOperations.length > 0) {
    try {
      const { error } = await supabase
        .from('bot_accounts')
        .delete()
        .in('id', disconnectOperations.map(op => op.accountId));

      if (error) {
        result.failed += disconnectOperations.length;
        result.errors.push(`Disconnect operations failed: ${error.message}`);
      } else {
        result.success += disconnectOperations.length;
      }
    } catch (error) {
      result.failed += disconnectOperations.length;
      result.errors.push(`Disconnect operations error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  return result;
};

/**
 * Export account data to CSV format
 */
export const exportAccountsData = (accounts: any[]): string => {
  const headers = [
    'Account ID',
    'User Name',
    'User Email',
    'Account Number',
    'Balance',
    'Volume Multiplier',
    'Status',
    'Account Type',
    'Reverse Position',
    'Broker',
    'Prop Firm',
    'Added Date',
    'Last Balance Update'
  ];

  const rows = accounts.map(account => [
    account.id,
    account.userName,
    account.userEmail,
    account.accountNumber,
    account.balance || 0,
    account.volumeMultiplier,
    account.status,
    account.isLive ? 'Live' : 'Demo',
    account.isReversePosition ? 'Yes' : 'No',
    account.broker || 'N/A',
    account.propFirm || 'N/A',
    account.addedDate,
    account.lastBalanceUpdate || 'N/A'
  ]);

  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');

  return csvContent;
};

/**
 * Download CSV file
 */
export const downloadCSV = (data: string, filename: string): void => {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};